import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ConfigProvider } from "antd";
import mnMN from "antd/locale/mn_MN";
import enUS from "antd/locale/en_US";
import dayjs from "dayjs";
import "dayjs/locale/mn";
import "dayjs/locale/en";

import translations from "./translations";

const STORAGE_KEY = "app_language";
const DEFAULT_LANGUAGE = "mn";

export const SUPPORTED_LANGUAGES = [
    { value: "mn", label: "Монгол", short: "MN" },
    { value: "en", label: "English", short: "EN" },
];

const antdLocales = { mn: mnMN, en: enUS };

const LanguageContext = createContext({
    language: DEFAULT_LANGUAGE,
    setLanguage: () => {},
    t: (key) => key,
});

function readStoredLanguage() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return SUPPORTED_LANGUAGES.some((item) => item.value === stored)
            ? stored
            : DEFAULT_LANGUAGE;
    } catch {
        return DEFAULT_LANGUAGE;
    }
}

/**
 * App-wide language selection (Mongolian / English).
 *
 * Also switches the Ant Design locale and the dayjs locale, so date pickers, pagination and
 * table texts follow the chosen language. The choice is remembered per browser.
 */
export function LanguageProvider({ children }) {
    const [language, setLanguageState] = useState(readStoredLanguage);

    useEffect(() => {
        dayjs.locale(language);

        try {
            localStorage.setItem(STORAGE_KEY, language);
        } catch {
            // A browser that blocks storage still gets a working language switch for the session.
        }
    }, [language]);

    const setLanguage = useCallback((next) => {
        if (SUPPORTED_LANGUAGES.some((item) => item.value === next)) {
            setLanguageState(next);
        }
    }, []);

    const t = useCallback(
        (key, fallback) => {
            const dictionary = translations[language] || translations[DEFAULT_LANGUAGE];
            if (dictionary[key] !== undefined) return dictionary[key];

            // Fall back to Mongolian so a key that is not translated yet still reads correctly.
            const base = translations[DEFAULT_LANGUAGE];
            if (base[key] !== undefined) return base[key];

            return fallback !== undefined ? fallback : key;
        },
        [language]
    );

    const value = useMemo(() => ({ language, setLanguage, t }), [language, setLanguage, t]);

    return (
        <LanguageContext.Provider value={value}>
            <ConfigProvider locale={antdLocales[language] || mnMN}>{children}</ConfigProvider>
        </LanguageContext.Provider>
    );
}

export function useTranslation() {
    return useContext(LanguageContext);
}
